import React, { useCallback, useState, useRef, useEffect } from 'react';
import { ModalGap } from '../../../../services/settings/appStyle/global.style';
import styled from 'styled-components';

const TextareaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 1px solid #fff;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  background-color: transparent;
  color: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #75dda0;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: white;
`;

const PreviewContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
`;

const PreviewTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: white;
`;

const PreviewList = styled.div`
  margin: 0;
`;

const PreviewHeader = styled.div`
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
  color: white;
`;

const PreviewItem = styled.div`
  margin-bottom: 4px;
  padding-left: 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  position: relative;

  &:before {
    content: '•';
    position: absolute;
    left: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const HelpText = styled.p`
  margin: 8px 0 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
`;

const AddPlanDetails = ({ planFields, setPlanFields }) => {
  const [textareaValue, setTextareaValue] = useState('');
  const textareaRef = useRef(null);
  const isInitialized = useRef(false);

  // Initialize textarea value from planFields only once
  useEffect(() => {
    if (!isInitialized.current && planFields && planFields.length > 0) {
      const hasValidFields = planFields.some((field) => field.value?.trim());

      if (hasValidFields) {
        const initialValue = planFields
          .filter((field) => field.value?.trim())
          .map((field, index) => {
            // First line is header, no bullet point
            if (index === 0) {
              return field.value;
            }
            // Rest get bullet points
            return `• ${field.value}`;
          })
          .join('\n');
        setTextareaValue(initialValue);
        isInitialized.current = true;
      }
    }
  }, [planFields]);

  // Convert textarea content to planFields format and update parent
  const updatePlanFields = useCallback(
    (text) => {
      const lines = text.split('\n');
      const newPlanFields = [];

      lines.forEach((line, index) => {
        // First line is always header, remove any bullets if present
        const cleanedLine = index === 0 ? line.trim() : line.replace(/^[•\-\*]\s*/, '').trim();

        if (cleanedLine) {
          newPlanFields.push({
            id: index + 1,
            value: cleanedLine,
          });
        }
      });

      // Ensure at least one empty field exists if all are removed
      if (newPlanFields.length === 0) {
        newPlanFields.push({ id: 1, value: '' });
      }

      // Always update parent state
      setPlanFields(newPlanFields);
    },
    [setPlanFields]
  );

  const handleTextareaChange = useCallback(
    (e) => {
      const value = e.target.value;
      setTextareaValue(value);
      updatePlanFields(value);
    },
    [updatePlanFields]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        // Check if we're on the first line (header)
        const beforeCursor = textareaValue.substring(0, start);
        const isFirstLine = !beforeCursor.includes('\n');

        // If on first line (header), just add new line without bullet
        // Otherwise add bullet point
        const newLineContent = isFirstLine ? '\n' : '\n• ';
        const newValue =
          textareaValue.substring(0, start) + newLineContent + textareaValue.substring(end);
        setTextareaValue(newValue);
        updatePlanFields(newValue);

        // Set cursor position after the new line (and bullet if added)
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + newLineContent.length;
          textarea.focus();
        }, 0);
      }
    },
    [textareaValue, updatePlanFields]
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const lines = pastedText.split('\n');

      const formattedLines = lines
        .map((line, index) => {
          const trimmed = line.trim();
          if (!trimmed) return '';

          // First line is header, no bullet
          if (index === 0) {
            return trimmed.replace(/^[•\-\*]\s*/, '');
          }

          // Add bullet if not present
          if (!trimmed.match(/^[•\-\*]\s/)) {
            return `• ${trimmed}`;
          }
          return trimmed;
        })
        .filter((line) => line);

      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newValue =
        textareaValue.substring(0, start) +
        formattedLines.join('\n') +
        textareaValue.substring(end);

      setTextareaValue(newValue);
      updatePlanFields(newValue);
    },
    [textareaValue, updatePlanFields]
  );

  // Get preview items for display
  const previewItems = planFields
    .filter((field) => field.value?.trim())
    .map((field) => field.value);

  return (
    <ModalGap>
      <TextareaWrapper>
        <Label htmlFor="planDetails">Plan Details</Label>
        <StyledTextarea
          ref={textareaRef}
          id="planDetails"
          value={textareaValue}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="First line is the header (no bullet point)
• Second line onwards will have bullet points
• Press Enter to create new lines
• You can also paste content and it will be formatted"
        />
        <HelpText>
          First line is the header. Press Enter to add bullet points for plan details.
        </HelpText>

        {previewItems.length > 0 && (
          <PreviewContainer>
            <PreviewTitle>Preview ({previewItems.length} items):</PreviewTitle>
            <PreviewList>
              {previewItems.map((item, index) =>
                index === 0 ? (
                  <PreviewHeader key={index}>{item}</PreviewHeader>
                ) : (
                  <PreviewItem key={index}>{item}</PreviewItem>
                )
              )}
            </PreviewList>
          </PreviewContainer>
        )}
      </TextareaWrapper>
    </ModalGap>
  );
};

export default AddPlanDetails;
